import { useState } from "react";
import type { WidgetStrings } from "./i18n";
import { joinWaitlist, type PartOfDay } from "./api/waitlist";

/**
 * Wachtlijst in de datum/tijd-stap, als er op de gekozen dag geen tijden meer zijn. Klapt
 * open na een klik; naam/e-mail/telefoon worden voorgevuld uit wat de klant al had ingevuld.
 */
export function WaitlistForm({
    t,
    apiBase,
    companySlug,
    serviceId,
    staffId,
    date,
    initialName,
    initialEmail,
    initialPhone,
}: {
    t: WidgetStrings;
    apiBase: string;
    companySlug: string;
    serviceId: number;
    staffId: number | null;
    date: string;
    initialName: string;
    initialEmail: string;
    initialPhone: string;
}) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState(initialName);
    const [email, setEmail] = useState(initialEmail);
    const [phone, setPhone] = useState(initialPhone);
    const [partOfDay, setPartOfDay] = useState<PartOfDay>("ANY");
    const [state, setState] = useState<"idle" | "sending" | "done">("idle");
    const [error, setError] = useState("");

    if (state === "done") {
        return <div className="bk-alert bk-alert-success">{t.waitlist.done}</div>;
    }

    if (!open) {
        return (
            <button type="button" className="bk-btn bk-btn-ghost" onClick={() => setOpen(true)}>
                {t.waitlist.cta}
            </button>
        );
    }

    const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
    const canSubmit = name.trim().length > 0 && emailValid && state !== "sending";

    async function submit() {
        if (!canSubmit) return;
        setError("");
        setState("sending");
        try {
            await joinWaitlist(apiBase, companySlug, {
                serviceId,
                staffId,
                date,
                partOfDay,
                customerName: name.trim(),
                customerEmail: email.trim(),
                customerPhone: phone.trim() || null,
            });
            setState("done");
        } catch (e) {
            const status = (e as { status?: number })?.status;
            setError(status === 409 ? t.waitlist.alreadyOnList : t.waitlist.failed);
            setState("idle");
        }
    }

    return (
        <div className="bk-waitlist">
            <p className="bk-page-copy" style={{ margin: 0 }}>{t.waitlist.intro}</p>

            <label className="bk-field">
                <span>{t.waitlist.partOfDayLabel}</span>
                <select
                    className="bk-input"
                    value={partOfDay}
                    onChange={(e) => setPartOfDay(e.target.value as PartOfDay)}
                >
                    {(["ANY", "MORNING", "AFTERNOON", "EVENING"] as const).map((p) => (
                        <option key={p} value={p}>{t.waitlist.partOfDay[p]}</option>
                    ))}
                </select>
            </label>

            <label className="bk-field">
                <span>{t.details.nameLabel}</span>
                <input className="bk-input" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={t.details.namePlaceholder} />
            </label>

            <label className="bk-field">
                <span>{t.details.emailLabel}</span>
                <input className="bk-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>

            <label className="bk-field">
                <span>{t.waitlist.phoneLabel}</span>
                <input className="bk-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>

            {error && <div className="bk-alert bk-alert-error">{error}</div>}

            <button type="button" className="bk-btn bk-btn-primary" disabled={!canSubmit} onClick={() => void submit()}>
                {state === "sending" ? t.waitlist.sending : t.waitlist.submit}
            </button>
        </div>
    );
}
